import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

/**
 * The pinned pager every list in this domain carries — the entity list's tabs,
 * the Activity feed and Traits. One hook, not a block copied into each page.
 *
 * It owns the rule that is easiest to get wrong: a narrowed list is shorter, so
 * the page the user was on may not exist in it, and an empty page reads as
 * "nothing here" rather than as a stale offset. Pass `queryKey` — everything
 * that narrows the request — and it returns to page 1 the moment that changes.
 *
 * Held as state, not a ref: this is React's documented "adjust state while
 * rendering" pattern, and a ref written during render is both flagged by
 * react-hooks/refs and unreliable — a render that never commits still mutates
 * it, so the reset can be silently skipped.
 *
 * Whether the pager SHOWS is the page's own call — the feed hides it with no
 * rows, Traits only once there is a second page — so it is deliberately not
 * decided here.
 *
 * @param {object} options
 * @param {number[]} options.pageSizeOptions — the first is the initial size.
 * @param {string} options.queryKey — everything that narrows the request.
 */
export function useListPagination({ pageSizeOptions, queryKey }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);

  const [lastQueryKey, setLastQueryKey] = useState(queryKey);
  if (lastQueryKey !== queryKey) {
    setLastQueryKey(queryKey);
    if (page !== 1) setPage(1);
  }

  const resetPage = useCallback(() => setPage(1), []);
  // The page the SERVER put us on (the one holding an event a link named, CT-5724).
  const goToPage = useCallback((next) => setPage(Math.max(1, next)), []);
  const handlePrevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const handleNextPage = useCallback(() => setPage((p) => p + 1), []);
  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, []);

  return useMemo(
    () => ({ page, pageSize, resetPage, goToPage, handlePrevPage, handleNextPage, handlePageSizeChange }),
    [page, pageSize, resetPage, goToPage, handlePrevPage, handleNextPage, handlePageSizeChange],
  );
}

/**
 * The pager's copy: "1-10" and the labels `Pagination` renders around it.
 *
 * Separate from `useListPagination` because it reads `meta`, which only exists
 * AFTER the request — while the page and page size it was made with have to
 * exist before. Calling both around the data hook keeps that order honest.
 *
 * @param {object} options
 * @param {{ totalItems: number, currentPage: number, perPage: number }} options.meta
 * @param {number} options.itemCount — rows on screen, which is the last page's
 *   real length rather than `perPage`.
 */
export function usePaginationLabels({ meta, itemCount }) {
  const { t } = useTranslation('unified-profiles');

  const rangeLabel = useMemo(() => {
    if (meta.totalItems === 0 || itemCount === 0) return '0-0';
    const start = (meta.currentPage - 1) * meta.perPage + 1;
    return `${start}-${start + itemCount - 1}`;
  }, [meta.currentPage, meta.perPage, meta.totalItems, itemCount]);

  const labels = useMemo(
    () => ({
      rowsPerPage: t('list.pagination.rowsPerPage'),
      // `list.pagination.items` is "{{range}} of {{total}} records" — passing
      // no values renders those placeholders literally.
      items: t('list.pagination.items', { range: rangeLabel, total: meta.totalItems }),
      previousPage: t('list.pagination.previousPage'),
      nextPage: t('list.pagination.nextPage'),
    }),
    [rangeLabel, t, meta.totalItems],
  );

  return useMemo(() => ({ rangeLabel, labels }), [rangeLabel, labels]);
}

export default useListPagination;
